import React, { useEffect, useState, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
// Stagger fade-in hook
function useFadeIn(delay: number) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return visible;
}
const FadeBlock = ({
  children,
  delay,
  className = ''
}: {
  children: React.ReactNode;
  delay: number;
  className?: string;
}) => {
  const visible = useFadeIn(delay);
  return <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'} ${className}`} data-id="element-1921">
      {children}
    </div>;
};
export function InitialReveal() {
  const navigate = useNavigate();
  const [loadingStage, setLoadingStage] = useState(0);
  const [name, setName] = useState('');
  const [copied, setCopied] = useState(false);
  const exposingLine = "You don't hesitate because you're unsure. You hesitate because you don't trust unfinished thinking.";
  useEffect(() => {
    const dob = sessionStorage.getItem('decodeDateOfBirth');
    const place = sessionStorage.getItem('decodePlaceOfBirth');
    const storedName = sessionStorage.getItem('decodeName');
    if (!dob || !place) {
      navigate('/decode/details');
      return;
    }
    if (storedName) setName(storedName);
    const t1 = setTimeout(() => setLoadingStage(1), 800);
    const t2 = setTimeout(() => setLoadingStage(2), 1800);
    const t3 = setTimeout(() => setLoadingStage(3), 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [navigate]);
  // LOADING SCREEN
  if (loadingStage < 3) {
    return <div className="min-h-screen bg-[#F8F7F4] flex flex-col items-center justify-center px-6" data-id="element-1922">
        <div className="space-y-4 text-center font-mono text-xs sm:text-sm uppercase tracking-widest text-[#888888]" data-id="element-1923">
          <p className={`transition-opacity duration-700 ${loadingStage >= 0 ? 'opacity-100' : 'opacity-0'}`} data-id="element-1924">
            Reading initial conditions...
          </p>
          <p className={`transition-opacity duration-700 ${loadingStage >= 1 ? 'opacity-100' : 'opacity-0'}`} data-id="element-1925">
            Mapping pattern clusters...
          </p>
          <p className={`transition-opacity duration-700 ${loadingStage >= 2 ? 'opacity-100' : 'opacity-0'}`} data-id="element-1926">
            Calibrating dominant bias...
          </p>
        </div>
      </div>;
  }
  return <>
      <Helmet data-id="element-1927">
        <title data-id="element-1928">Initial System Reveal | Lazy Monk Labs</title>
        <meta name="robots" content="noindex" data-id="element-1929" />
      </Helmet>

      <div className="min-h-screen bg-[#F8F7F4] text-[#444444] selection:bg-[#B89C5D]/20" data-id="element-1930">
        {/* MINIMAL HEADER — logo only */}
        <header className="fixed top-0 left-0 right-0 z-50 px-6 py-5 bg-[#F8F7F4]/95 backdrop-blur-sm" data-id="element-1931">
          <Link to="/" className="flex items-center gap-2 w-fit hover:opacity-70 transition-opacity" data-id="element-1932">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs" className="h-6 w-auto" data-id="element-1933" />
          </Link>
        </header>

        <main className="pt-28 pb-32 px-6 sm:px-8" data-id="element-1934">
          <div className="max-w-[680px] mx-auto" data-id="element-1935">
            {/* ARRIVAL MOMENT */}
            <FadeBlock delay={100} data-id="element-1936">
              <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em] text-center mb-20" data-id="element-1937">
                Initial System Reveal
              </p>
            </FadeBlock>

            {/* ❤️ HEART ANCHOR */}
            <FadeBlock delay={300} className="mb-20" data-id="element-1938">
              <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em] mb-8" data-id="element-1939">
                ❤ Heart Anchor — Core Formation
              </p>

              <p className="text-base text-[#888888] font-light leading-relaxed mb-8" data-id="element-1940">
                {name ? `${name}'s` : 'Your'} core emotional formation was built
                around:
              </p>

              <div className="space-y-5 mb-10" data-id="element-1941">
                <p className="text-2xl sm:text-3xl text-[#111111] font-medium leading-tight" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-1942">
                  Responsibility before freedom.
                </p>
                <p className="text-2xl sm:text-3xl text-[#111111] font-medium leading-tight" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-1943">
                  Clarity before expression.
                </p>
              </div>

              <div className="space-y-3 pl-5 border-l border-[#B89C5D]/30 mb-10" data-id="element-1944">
                <p className="text-base text-[#555555] font-light leading-relaxed" data-id="element-1945">
                  You were shaped to think before you move.
                </p>
                <p className="text-base text-[#555555] font-light leading-relaxed" data-id="element-1946">
                  You don't rush decisions. You replay conversations. You prefer
                  being prepared over being spontaneous.
                </p>
                <p className="text-base text-[#555555] font-light leading-relaxed" data-id="element-1947">
                  You don't call it caution. You call it responsibility.
                </p>
              </div>

              <p className="text-lg sm:text-xl text-[#555555] italic leading-relaxed text-center px-4" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1948">
                "If I don't think this through properly, it could cost me."
              </p>
            </FadeBlock>

            {/* ✦ GENIUS + SHADOW */}
            <FadeBlock delay={500} className="mb-20" data-id="element-1949">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-px bg-[#E8E5DF]" data-id="element-1950">
                {/* Genius */}
                <div className="bg-[#F8F7F4] p-8 md:p-10 space-y-4" data-id="element-1951">
                  <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-1952">
                    Genius Mode
                  </p>
                  <div className="space-y-3 text-base text-[#444444] font-light leading-relaxed" data-id="element-1953">
                    <p data-id="element-1954">
                      You see what others miss because you slow down when they
                      speed up.
                    </p>
                    <p data-id="element-1955">
                      You catch the flaw in the plan before it becomes the
                      crisis.
                    </p>
                    <p data-id="element-1956">
                      You hold the room together by staying composed when others
                      fracture.
                    </p>
                  </div>
                </div>

                {/* Shadow */}
                <div className="bg-[#F0EDE8] p-8 md:p-10 space-y-4" data-id="element-1957">
                  <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-1958">
                    Shadow Mode
                  </p>
                  <div className="space-y-3 text-base text-[#3A3A3A] font-light leading-relaxed" data-id="element-1959">
                    <p data-id="element-1960">You delay decisions until the window closes.</p>
                    <p data-id="element-1961">You over-prepare for conversations that never happen.</p>
                    <p data-id="element-1962">
                      You exhaust yourself holding things together that others
                      have already let go.
                    </p>
                  </div>
                </div>
              </div>
            </FadeBlock>

            {/* ⟳ LOOP UNDER PRESSURE */}
            <FadeBlock delay={700} className="mb-20" data-id="element-1963">
              <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em] text-center mb-10" data-id="element-1964">
                Under Pressure
              </p>

              <div className="flex flex-col items-center space-y-2 font-mono text-xs sm:text-sm text-[#444444] tracking-wider" data-id="element-1965">
                <span data-id="element-1966">Uncertainty</span>
                <span className="text-[#B89C5D] text-base" data-id="element-1967">↓</span>
                <span data-id="element-1968">Analysis</span>
                <span className="text-[#B89C5D] text-base" data-id="element-1969">↓</span>
                <span data-id="element-1970">Delay</span>
                <span className="text-[#B89C5D] text-base" data-id="element-1971">↓</span>
                <span data-id="element-1972">More analysis</span>
                <span className="text-[#B89C5D] text-base" data-id="element-1973">↓</span>
                <span className="text-[#888888]" data-id="element-1974">Even more pressure</span>
              </div>
            </FadeBlock>

            {/* MASK vs TRUE SELF */}
            <FadeBlock delay={900} className="mb-20" data-id="element-1975">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 mb-10" data-id="element-1976">
                <div className="space-y-4" data-id="element-1977">
                  <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-1978">
                    The Mask
                  </p>
                  <div className="space-y-3 text-base text-[#555555] font-light leading-relaxed" data-id="element-1979">
                    <p data-id="element-1980">Composed. Measured. Reliable.</p>
                    <p data-id="element-1981">Always has a plan. Never seems rattled.</p>
                    <p data-id="element-1982">The one people come to when things fall apart.</p>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-1983">
                  <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-1984">
                    The True Self
                  </p>
                  <div className="space-y-3 text-base text-[#555555] font-light leading-relaxed" data-id="element-1985">
                    <p data-id="element-1986">Constantly recalibrating. Rarely at rest.</p>
                    <p data-id="element-1987">Needs more certainty than the situation provides.</p>
                    <p data-id="element-1988">Carries more than anyone knows.</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-[#888888] text-center italic" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-1989">
                {name ? `${name} looks` : 'You look'} composed. Inside, it's
                constant recalibration.
              </p>
            </FadeBlock>

            {/* 🪓 BRUTAL TRUTH */}
            <FadeBlock delay={1100} className="mb-16" data-id="element-1990">
              <div className="pt-4" data-id="element-1991">
                <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em] mb-10" data-id="element-1992">
                  🪓 Brutal Truth
                </p>

                <p className="text-base text-[#555555] font-light leading-relaxed mb-8" data-id="element-1993">
                  Left unchecked: high competence, low aliveness.
                </p>

                <div className="space-y-0 mb-10" data-id="element-1994">
                  <p className="text-2xl sm:text-3xl text-[#111111] font-medium leading-[1.3]" style={{
                  fontFamily: 'Georgia, serif'
                }} data-id="element-1995">
                    {exposingLine.split('. ')[0]}.
                  </p>
                  <p className="text-2xl sm:text-3xl text-[#111111] font-medium leading-[1.3] mt-4" style={{
                  fontFamily: 'Georgia, serif'
                }} data-id="element-1996">
                    {exposingLine.split('. ')[1]}
                  </p>
                </div>

                <p className="text-sm text-[#AAAAAA] font-light" data-id="element-1997">
                  This is one layer of your pattern.
                </p>
              </div>
            </FadeBlock>

            {/* SHARE — subtle, after brutal truth */}
            <FadeBlock delay={1200} className="mb-16" data-id="element-1998">
              <div className="py-8 border-t border-[#E8E5DF]" data-id="element-1999">
                <p className="text-sm text-[#888888] italic text-center mb-5" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-2000">
                  If this felt uncomfortably accurate, you already know who else
                  needs to read it.
                </p>
                <div className="flex justify-center" data-id="element-2001">
                  <button onClick={() => {
                  navigator.clipboard.writeText(`"${exposingLine}" — lazymonklabs.com/decode/details`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }} className="text-[10px] font-mono text-[#AAAAAA] hover:text-[#555555] transition-colors uppercase tracking-[0.2em]" data-id="element-2002">
                    {copied ? '✓ Copied' : 'Copy this line'}
                  </button>
                </div>
              </div>
            </FadeBlock>

            {/* UNLOCK */}
            <FadeBlock delay={1300} data-id="element-2003">
              <div className="pt-8 border-t border-[#E8E5DF]" data-id="element-2004">
                <p className="text-sm text-[#888888] font-light text-center mb-10 leading-relaxed" data-id="element-2005">
                  There's more underneath this.
                </p>

                <div className="flex flex-col items-center gap-4" data-id="element-2006">
                  <Link to="/decode/personal" className="w-full sm:w-auto px-10 py-5 bg-[#111111] text-[#F8F7F4] text-sm uppercase tracking-widest hover:bg-[#222222] transition-colors text-center" data-id="element-2007">
                    Unlock the full decode — ₹99
                  </Link>
                  <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.15em]" data-id="element-2008">
                    Secure. Instant. Yours to download.
                  </p>
                </div>
              </div>
            </FadeBlock>
          </div>
        </main>
      </div>
    </>;
}